import React from "react";
import { useI18n } from "../lib/i18n.jsx";
import { ShieldCheck } from "lucide-react";

const CONTROLLER = {
  name: "TIOUW.com B.V.",
  address: "Binderij 7-L, 1185 ZH Amstelveen, Nederland",
  kvk: "05076640",
  email: "joost@tiouw.com",
  website: "www.tiouw.com",
};

export default function PrivacyPage() {
  const { t, lang } = useI18n();
  const isNL = lang === "nl";
  const lastUpdated = "2026-07-06";

  return (
    <main data-testid="privacy-page" className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20">
      <p className="label-small text-[#8B3A3A] mb-4 flex items-center gap-2">
        <ShieldCheck size={12} /> {isNL ? "Privacyverklaring" : "Privacy Policy"}
      </p>
      <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-[#1A1918] mb-4">
        {isNL ? "Privacyverklaring." : "Privacy Policy."}
      </h1>
      <p className="text-xs font-mono uppercase tracking-[0.22em] text-[#7A7061] mb-10">
        {isNL ? `Laatst bijgewerkt: ${lastUpdated}` : `Last updated: ${lastUpdated}`}
      </p>

      <article className="prose prose-slate max-w-none text-[#1A1918] space-y-6">
        {isNL ? <Dutch controller={CONTROLLER} /> : <English controller={CONTROLLER} />}
      </article>
    </main>
  );
}

/* ---------- Section building block ---------- */
function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-display text-2xl mt-8 mb-3 text-[#1A1918]">{title}</h2>
      <div className="text-[#4A4238] leading-relaxed text-[15px] space-y-3">{children}</div>
    </section>
  );
}

function ControllerBox({ c }) {
  return (
    <address className="not-italic font-mono text-sm text-[#1A1918] border-l-2 border-[#8B3A3A] pl-4 my-4">
      <div><strong>{c.name}</strong></div>
      <div>{c.address}</div>
      <div>KvK: {c.kvk}</div>
      <div>Email: <a className="underline underline-offset-2" href={`mailto:${c.email}`}>{c.email}</a></div>
      <div>Website: <a className="underline underline-offset-2" href={`https://${c.website}`} target="_blank" rel="noopener noreferrer">{c.website}</a></div>
    </address>
  );
}

/* ---------- Dutch ---------- */
function Dutch({ controller }) {
  return (
    <>
      <p>
        Deze privacyverklaring beschrijft hoe <strong>NLP Magick</strong> (de applicatie op
        <code className="mx-1 font-mono">app.nlpmagick.nl</code>) omgaat met persoonsgegevens
        in overeenstemming met de Algemene Verordening Gegevensbescherming (AVG / GDPR).
      </p>

      <Section title="1. Verwerkingsverantwoordelijke">
        <p>De verwerkingsverantwoordelijke voor deze applicatie is:</p>
        <ControllerBox c={controller} />
        <p>
          Voor vragen over deze verklaring, verzoeken tot inzage, correctie of verwijdering
          van je gegevens kun je contact opnemen via bovenstaand e-mailadres.
        </p>
      </Section>

      <Section title="2. Welke gegevens verwerken wij?">
        <p>Wij verwerken uitsluitend de gegevens die je zelf aan ons verstrekt of die noodzakelijk zijn voor de werking van de applicatie:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Accountgegevens</strong>: naam, e-mailadres, versleuteld wachtwoord (bcrypt).</li>
          <li><strong>Graad</strong>: de door de Adept toegekende inwijdingsgraad.</li>
          <li><strong>Toestemmingen</strong>: tijdstip waarop je akkoord bent gegaan met deze privacyverklaring en de gebruiksvoorwaarden.</li>
          <li><strong>Dagboekregels</strong>: door jou vastgelegde rituelen en vrije notities (strikt privé — alleen zichtbaar voor jou en de Adept in geval van technisch beheer).</li>
          <li><strong>Pantheon</strong>: godheden die je hebt aangemaakt, geclaimd of geliked. Aangemaakte inhoud is zichtbaar voor andere ingewijden.</li>
          <li><strong>Optionele 2FA-gegevens</strong>: als je twee-factor-authenticatie inschakelt, bewaren wij een versleuteld TOTP-geheim en gehashte reservecodes.</li>
          <li><strong>Technisch essentiële cookie</strong>: een httpOnly authenticatie-cookie zodat je ingelogd blijft. Deze cookie is strikt noodzakelijk en wordt niet gebruikt voor tracking.</li>
        </ul>
      </Section>

      <Section title="3. Grondslag en doel">
        <p>Wij verwerken je gegevens op basis van:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Uitvoering van de overeenkomst</strong> (art. 6 lid 1 sub b AVG): je account, je dagboek en de kernfunctionaliteit van de applicatie.</li>
          <li><strong>Gerechtvaardigd belang</strong> (art. 6 lid 1 sub f AVG): beveiliging van je account, waaronder optionele 2FA.</li>
          <li><strong>Toestemming</strong> (art. 6 lid 1 sub a AVG): je expliciete akkoord bij registratie, dat je op elk moment kunt intrekken door je account te verwijderen.</li>
        </ul>
        <p>Wij gebruiken je gegevens niet voor reclame en delen ze niet met derden voor commerciële doeleinden.</p>
      </Section>

      <Section title="4. Bewaartermijn">
        <p>
          Je gegevens worden bewaard zolang je een actief account hebt. Wanneer je je account
          verwijdert, worden je e-mailadres, wachtwoord, dagboekregels en 2FA-geheimen
          onmiddellijk en definitief verwijderd. Godheden die je in het Pantheon hebt
          aangemaakt blijven behouden maar worden geanonimiseerd (de link naar jou wordt
          verbroken). Backups worden binnen 30 dagen overschreven.
        </p>
      </Section>

      <Section title="5. Jouw rechten (AVG art. 15–22)">
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Inzage</strong> — vraag een kopie van jouw gegevens op via “Mijn gegevens downloaden” in je accountpagina.</li>
          <li><strong>Rectificatie</strong> — pas je naam of e-mail zelf aan in je account, of vraag ons om correctie.</li>
          <li><strong>Verwijdering (“recht op vergetelheid”)</strong> — verwijder je account zelf via “Account verwijderen”.</li>
          <li><strong>Beperking en bezwaar</strong> — mail ons.</li>
          <li><strong>Overdraagbaarheid</strong> — de export levert je gegevens in een leesbaar JSON-formaat.</li>
          <li><strong>Klacht bij toezichthouder</strong> — je hebt het recht een klacht in te dienen bij de <a className="underline underline-offset-2" href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer">Autoriteit Persoonsgegevens</a>.</li>
        </ul>
      </Section>

      <Section title="6. Beveiliging">
        <p>
          Wachtwoorden worden opgeslagen met bcrypt. Alle communicatie verloopt via HTTPS.
          Het authenticatie-cookie is <code>httpOnly</code> en <code>Secure</code>. TOTP-geheimen en
          reservecodes worden versleuteld resp. gehasht opgeslagen. Toegang tot de database
          is beperkt tot noodzakelijke technische systemen.
        </p>
      </Section>

      <Section title="7. Doorgifte buiten de EER">
        <p>
          De applicatie wordt gehost binnen de EU/EER. Voor onderdelen die door onze
          hostingpartner (Emergent) buiten de EER worden verwerkt, gelden de standaard
          contractbepalingen van de Europese Commissie.
        </p>
      </Section>

      <Section title="8. Wijzigingen">
        <p>
          Deze verklaring kan worden bijgewerkt. Wij melden materiële wijzigingen via de
          applicatie. De datum bovenaan geeft de laatste wijziging aan.
        </p>
      </Section>
    </>
  );
}

/* ---------- English ---------- */
function English({ controller }) {
  return (
    <>
      <p>
        This privacy policy describes how <strong>NLP Magick</strong> (the application at
        <code className="mx-1 font-mono">app.nlpmagick.nl</code>) handles personal data in
        accordance with the General Data Protection Regulation (GDPR / AVG).
      </p>

      <Section title="1. Data Controller">
        <p>The data controller for this application is:</p>
        <ControllerBox c={controller} />
        <p>For questions about this policy, or to request access, correction or deletion of your data, please contact us at the email address above.</p>
      </Section>

      <Section title="2. What data do we process?">
        <p>We only process data you provide yourself or that is strictly necessary for the application to function:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Account data</strong>: name, email address, hashed password (bcrypt).</li>
          <li><strong>Grade</strong>: the initiatory grade assigned by the Adept.</li>
          <li><strong>Consent</strong>: the timestamps at which you accepted this Privacy Policy and the Terms of Use.</li>
          <li><strong>Diary entries</strong>: the rituals and free-form notes you record (strictly private — visible only to you, and to the Adept for technical administration).</li>
          <li><strong>Pantheon</strong>: gods you create, claim or like. Created content is visible to other initiates.</li>
          <li><strong>Optional 2FA data</strong>: if you enable two-factor authentication, we store an encrypted TOTP secret and hashed backup codes.</li>
          <li><strong>Strictly necessary cookie</strong>: an httpOnly authentication cookie so you stay signed in. It is not used for tracking.</li>
        </ul>
      </Section>

      <Section title="3. Legal basis and purpose">
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Performance of contract</strong> (GDPR art. 6(1)(b)): your account, diary and the core functionality of the application.</li>
          <li><strong>Legitimate interest</strong> (GDPR art. 6(1)(f)): account security, including optional 2FA.</li>
          <li><strong>Consent</strong> (GDPR art. 6(1)(a)): your explicit consent at registration, which you can withdraw at any time by deleting your account.</li>
        </ul>
        <p>We do not use your data for advertising and do not share it with third parties for commercial purposes.</p>
      </Section>

      <Section title="4. Retention">
        <p>
          Your data is kept for as long as your account is active. When you delete your
          account, your email address, password, diary entries and 2FA secrets are
          immediately and permanently removed. Gods you created in the Pantheon remain but
          are anonymised (the link to you is severed). Backups are overwritten within 30 days.
        </p>
      </Section>

      <Section title="5. Your rights (GDPR art. 15–22)">
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Access</strong> — request a copy of your data via “Download my data” on your account page.</li>
          <li><strong>Rectification</strong> — edit your name or email in your account, or ask us to correct data.</li>
          <li><strong>Erasure (“right to be forgotten”)</strong> — delete your own account via “Delete my account”.</li>
          <li><strong>Restriction and objection</strong> — email us.</li>
          <li><strong>Portability</strong> — the export is a machine-readable JSON file.</li>
          <li><strong>Right to lodge a complaint</strong> — you have the right to lodge a complaint with the <a className="underline underline-offset-2" href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer">Dutch Data Protection Authority</a>.</li>
        </ul>
      </Section>

      <Section title="6. Security">
        <p>
          Passwords are stored with bcrypt. All traffic uses HTTPS. The auth cookie is <code>httpOnly</code>
          and <code>Secure</code>. TOTP secrets and backup codes are stored encrypted and hashed
          respectively. Database access is restricted to essential technical systems.
        </p>
      </Section>

      <Section title="7. Transfers outside the EEA">
        <p>
          The application is hosted within the EU/EEA. Where our hosting partner (Emergent)
          processes any component outside the EEA, the European Commission’s Standard
          Contractual Clauses apply.
        </p>
      </Section>

      <Section title="8. Changes">
        <p>
          This policy may be updated. Material changes will be announced in-app. The
          “last updated” date at the top reflects the most recent revision.
        </p>
      </Section>
    </>
  );
}
